import { Container } from '@/components/layout/container/container';
import { Link } from '@/components/ui/link';
import RichTextViewer from '@/components/ui/rich-text-viewer/rich-text-viewer';
import { cn } from '@/lib/utils';
import type { FormContent } from '@/lib/utils/form-data';
import type { Document } from '@contentful/rich-text-types';
import { Info } from 'lucide-react';
import styles from './embedded-blocks.module.scss';

type EmbeddedNoteCta = {
  label: string;
  url: string;
  type: 'Button' | 'Link';
  buttonStyle?: 'Pink' | 'White';
  newWindow?: boolean;
  destination?: 'URL' | 'Page' | 'Form';
  ctaId?: string;
  formContent?: FormContent;
};

type EmbeddedNoteProps = {
  id?: string;
  heading?: string;
  content: Document;
  variant?: 'important' | 'info' | 'featured';
  showIcon?: boolean;
  /** When true, renders with grey background and left-aligned text (resource/document context) */
  inline?: boolean;
  cta?: EmbeddedNoteCta;
};

const NoteCta = ({ cta }: { cta: EmbeddedNoteCta }) => (
  <Link
    label={cta.label}
    url={cta.url}
    type={cta.type}
    buttonStyle={cta.buttonStyle}
    withArrow
    target={cta.newWindow ? '_blank' : undefined}
    rel={cta.newWindow ? 'noopener noreferrer' : undefined}
    destination={cta.destination}
    ctaId={cta.ctaId}
    {...cta.formContent}
  />
);

export const EmbeddedNote = ({
  id,
  heading,
  content,
  variant = 'info',
  showIcon = false,
  inline = false,
  cta,
}: EmbeddedNoteProps) => {
  if (variant === 'important') {
    return (
      <Container>
        <div id={id} className={styles.important}>
          <div className={styles.important__sidebar} />
          <div className={styles.important__noteBox}>
            {showIcon && (
              <div className={styles.important__icon}>
                <Info size={24} />
              </div>
            )}
            <div className={styles.important__body}>
              {heading && <h3 className="h5 text-text-dark-purple">{heading}</h3>}
              <div className={styles.important__content}>
                <RichTextViewer content={content} />
              </div>
              {cta && <NoteCta cta={cta} />}
            </div>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <div
        id={id}
        className={cn(
          styles.note,
          styles[`note--${variant}`],
          showIcon && styles['note--with-icon'],
          inline && styles['note--inline'],
        )}
      >
        {showIcon && (
          <div
            className={cn(styles.note__icon, variant === 'featured' && styles['note__icon--white'])}
          >
            <Info size={24} />
          </div>
        )}
        <div className={styles.note__body}>
          {heading && (
            <h3 className={variant !== 'featured' ? 'h5 text-text-dark-purple' : 'h5'}>
              {heading}
            </h3>
          )}
          <div className={styles.note__content}>
            <RichTextViewer content={content} />
          </div>
          {cta && <NoteCta cta={cta} />}
        </div>
      </div>
    </Container>
  );
};
